// src/pages/ConnectAuthorize.jsx
// The consent screen for connecting an AI assistant to a SurFox account (connector OAuth, decision B, 2026-10-03).
// `connector-oauth` /authorize sends the assistant's browser here as /connect/authorize?request=<id>; the user signs in
// if needed, reads what the assistant would be able to read, and approves or denies. The WORDING comes from the server
// (reads, cannot, notes in the /request reply) so it lives in one place and can never disagree with what is enforced.
//
// Deliberately OUTSIDE <ProtectedRoute>: ProtectedRoute sends a signed-out user to /login without remembering where they
// were going, which would drop the request id (it only lives 10 minutes). This page does its own session check and hands
// /login an explicit returnTo, the same way ExtensionAuth does.
//
// Everything from the server is rendered as TEXT (never as HTML). The assistant's name is chosen by the assistant itself.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import supabase from '../lib/supabaseClient';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/connector-oauth`;

// Plain-words messages for every refusal. The server's own error text is never shown, so a wording change on the
// server cannot put an engineer's sentence on a customer's screen. Keys are used by the tests.
export const FAILURES = {
  not_admin: {
    title: 'Only administrators can connect an assistant',
    body: 'Ask an administrator of your SurFox account to approve this connection.',
  },
  not_enabled: {
    title: 'Not available for your account yet',
    body: 'Connecting an AI assistant is not turned on for your SurFox account. Nothing was shared.',
  },
  reviewer: {
    title: 'Demo accounts cannot connect assistants',
    body: 'Sign in with your own SurFox account to connect an assistant.',
  },
  inactive: {
    title: 'This account is suspended',
    body: 'Contact support@getsurfox.com if you think this is a mistake. Nothing was shared.',
  },
  expired: {
    title: 'This request has expired',
    body: 'Connection requests last 10 minutes and can be used once. Go back to your assistant and start the connection again.',
  },
  invalid: {
    title: 'This link is not valid',
    body: 'Go back to your assistant and start the connection again.',
  },
  failed: {
    title: 'Something went wrong',
    body: 'We could not complete this just now. Nothing was shared. Try again in a moment.',
  },
  unsafe: {
    title: 'We could not send you back safely',
    body: 'Nothing was shared with the assistant. Go back to your assistant and start the connection again.',
  },
};

// Server answer -> which message. 401 is not a failure: the session is gone, so the user is asked to sign in again.
export function classifyFailure(status, code) {
  if (status === 401) return 'signin';
  if (status === 403) {
    if (code === 'role_not_allowed') return 'not_admin';
    if (code === 'connector_not_enabled') return 'not_enabled';
    if (code === 'REVIEWER_ACCOUNT') return 'reviewer';
    if (code === 'account_inactive') return 'inactive';
    return 'not_enabled';   // any other refusal reads the same to the customer: nothing happened
  }
  if (status === 404 || status === 409) return 'expired';
  if (status === 400) return 'invalid';
  return 'failed';
}

// The server sends the browser to the assistant's registered redirect (Claude's callback, or a loopback address on the
// user's own computer). Before leaving, make sure that is the host we were told about and a plain web or loopback URL.
export function isSafeRedirect(redirectTo, expectedHost, expectLoopback) {
  try {
    const u = new URL(redirectTo);
    if (!expectedHost || u.hostname !== expectedHost) return false;
    const loopback = u.hostname === 'localhost' || u.hostname === '127.0.0.1';
    if (expectLoopback !== loopback) return false;
    return loopback ? u.protocol === 'http:' || u.protocol === 'https:' : u.protocol === 'https:';
  } catch {
    return false;
  }
}

async function callServer(method, path, token, body) {
  const res = await fetch(`${FUNCTION_URL}/${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY || '',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data = null;
  try { data = await res.json(); } catch { /* not JSON */ }
  return { status: res.status, ok: res.ok, data };
}

export default function ConnectAuthorize() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestId = params.get('request') || '';
  // checking | signin | loading | ready | submitting | redirecting | failure
  const [phase, setPhase] = useState('checking');
  const [failure, setFailure] = useState(null);
  const [info, setInfo] = useState(null);
  const submitted = useRef(false);

  const fail = (kind) => { setFailure(kind); setPhase('failure'); };

  const load = useCallback(async () => {
    if (!UUID_RE.test(requestId)) { fail('invalid'); return; }
    setPhase('checking');
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      if (!session?.access_token) { setPhase('signin'); return; }
      setPhase('loading');
      const r = await callServer('GET', `request?id=${encodeURIComponent(requestId)}`, session.access_token);
      if (r.ok && r.data && r.data.request_id) { setInfo(r.data); setPhase('ready'); return; }
      const kind = classifyFailure(r.status, r.data?.code);
      if (kind === 'signin') setPhase('signin'); else fail(kind);
    } catch (e) {
      console.error('Connect authorize load error:', e);
      fail('failed');
    }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const goToLogin = () => {
    navigate('/login', { state: { returnTo: `/connect/authorize?request=${requestId}` } });
  };

  const decide = async (decision) => {
    if (submitted.current) return;          // a double click must not decide twice
    submitted.current = true;
    setPhase('submitting');
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      if (!session?.access_token) { submitted.current = false; setPhase('signin'); return; }
      const r = await callServer('POST', 'decide', session.access_token, { request_id: info.request_id, decision });
      if (!r.ok || !r.data?.redirect_to) {
        const kind = classifyFailure(r.status, r.data?.code);
        if (kind === 'signin') { submitted.current = false; setPhase('signin'); } else fail(kind);
        return;
      }
      if (!isSafeRedirect(r.data.redirect_to, info.redirect_host, info.redirect_is_loopback === true)) {
        console.error('Connect authorize: refused an unexpected redirect target');
        fail('unsafe');
        return;
      }
      setPhase('redirecting');
      window.location.assign(r.data.redirect_to);
    } catch (e) {
      console.error('Connect authorize decide error:', e);
      fail('failed');
    }
  };

  const assistant = info?.client_name || 'This assistant';
  const account = info?.tenant_name ? `${info.tenant_name}'s` : 'your';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
        <img src="/newSurFoxLogo1.png" alt="SurFox AI" className="h-14 mx-auto mb-6 object-contain" />

        {(phase === 'checking' || phase === 'loading') && (
          <div className="text-center" role="status">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading this request...</p>
          </div>
        )}

        {phase === 'signin' && (
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Sign in to continue</h2>
            <p className="text-gray-600 mb-6">
              Sign in to your SurFox account to review this connection request. You will come straight back here afterwards.
            </p>
            <button onClick={goToLogin} className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition">
              Sign in to SurFox
            </button>
          </div>
        )}

        {(phase === 'ready' || phase === 'submitting') && info && (
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-1 break-words">Connect {assistant} to SurFox</h2>
            <p className="text-gray-700 mb-4 break-words">
              <span className="font-medium">{assistant}</span> wants to read the data in <span className="font-medium">{account}</span> SurFox account:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-800 mb-4">
              {(info.reads || []).map((line) => <li key={line}>{line}</li>)}
            </ul>
            <p className="font-medium text-gray-900 mb-4">{info.cannot}</p>
            <ul className="space-y-2 text-sm text-gray-600 mb-4 border-t border-gray-100 pt-4">
              {(info.notes || []).map((line) => <li key={line}>{line}</li>)}
            </ul>
            <p className="text-xs text-gray-500 mb-6">
              {info.redirect_is_loopback
                ? `This assistant runs on your computer and will connect from ${info.redirect_host}.`
                : `After you decide, you will be sent back to ${info.redirect_host}.`}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => decide('approve')}
                disabled={phase === 'submitting'}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => decide('deny')}
                disabled={phase === 'submitting'}
                className="flex-1 bg-white text-gray-700 py-3 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition disabled:opacity-50"
              >
                Deny
              </button>
            </div>
          </div>
        )}

        {phase === 'redirecting' && (
          <div className="text-center" role="status">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Sending you back to {assistant}...</p>
          </div>
        )}

        {phase === 'failure' && failure && (
          <div className="text-center" role="alert">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{FAILURES[failure].title}</h2>
            <p className="text-gray-600 mb-4">{FAILURES[failure].body}</p>
            {failure === 'failed' && (
              <button onClick={() => window.location.reload()} className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 transition">
                Try again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
